// Панель разбора запуска поверх игры — снимается скриншотом в живом Telegram («Open» и «Играть»). Открывается, если в
// адресе или в startapp есть debug, или долгим нажатием (0.8 с) на кружок уровня в шапке. Значений initData и адреса
// не показывает: только длины, ключи, версии и статусы.
import { signal } from '@preact/signals';
import { useApp, BUILD_TAG } from '../app/app.js';

export const debugOpen = signal(false);

/** Последнее окно счёта: как открыто и чем кончилось (store.tsx). */
export const lastInvoice = signal<{ path: string; error?: string; status?: string; via?: string; ms?: number; fallback?: boolean } | null>(null);

/** Сколько ждали telegram-web-app.js на входе (main.tsx). */
export const bootWait = signal<number | null>(null);

type Boot = { hashKeys: string[]; script: string; scriptMs: number | null; debug?: boolean };

/** debug в адресе (?debug, #…debug) или в startapp (tgWebAppStartParam / start_param). */
export function debugRequested(search: string, boot: Boot | undefined, startParam: string | undefined): boolean {
  return /(^|[?&])debug(=|&|$)/.test(search) || !!boot?.debug || /debug/i.test(startParam ?? '');
}

/** Тапы подряд для панели: 5 за 2 с. Переход на героя — после паузы TAP_PAUSE_MS без нового тапа. */
export const DEBUG_TAPS = 5;
export const DEBUG_TAPS_MS = 2000;
export const TAP_PAUSE_MS = 450;

/**
 * Кружок уровня (аватар в шапке): 5 быстрых тапов за 2 с или долгое нажатие 0.8 с — панель. Обычный тап уводит на
 * героя не сразу, а после паузы 0.45 с без нового тапа — первые тапы серии никуда не уводят.
 */
export function installDebugGesture(doc: Document = document, holdMs = 800): () => void {
  const SEL = '[data-testid="home-char"]';
  let hold: ReturnType<typeof setTimeout> | null = null;
  let pause: ReturnType<typeof setTimeout> | null = null;
  let held = false;
  let passing = false; // наш отложенный click — пропустить к обработчику аватара
  let taps: number[] = [];
  const clearHold = () => { if (hold) clearTimeout(hold); hold = null; };
  const open = () => { debugOpen.value = true; taps = []; if (pause) clearTimeout(pause); pause = null; };
  const down = (e: PointerEvent) => {
    if (!(e.target as Element | null)?.closest?.(SEL)) return;
    held = false;
    clearHold();
    hold = setTimeout(() => { hold = null; held = true; open(); }, holdMs);
  };
  const click = (e: MouseEvent) => {
    const el = (e.target as Element | null)?.closest?.(SEL) as HTMLElement | null;
    if (!el) return;
    if (passing) { passing = false; return; }
    e.preventDefault();
    e.stopPropagation();
    if (held) { held = false; return; }
    const now = Date.now();
    taps = [...taps.filter((t) => now - t < DEBUG_TAPS_MS), now];
    if (taps.length >= DEBUG_TAPS) { open(); return; }
    if (pause) clearTimeout(pause);
    pause = setTimeout(() => { pause = null; taps = []; passing = true; el.click(); }, TAP_PAUSE_MS);
  };
  doc.addEventListener('pointerdown', down, true);
  doc.addEventListener('pointerup', clearHold, true);
  doc.addEventListener('pointercancel', clearHold, true);
  doc.addEventListener('click', click, true);
  return () => {
    clearHold();
    if (pause) clearTimeout(pause);
    doc.removeEventListener('pointerdown', down, true);
    doc.removeEventListener('pointerup', clearHold, true);
    doc.removeEventListener('pointercancel', clearHold, true);
    doc.removeEventListener('click', click, true);
  };
}

export function DebugPanel() {
  const app = useApp();
  if (!debugOpen.value) return null;
  const tg = (window as { Telegram?: { WebApp?: { initData?: string; version?: string; platform?: string; isFullscreen?: boolean; isExpanded?: boolean } } }).Telegram;
  const boot = (window as { __tgBoot?: Boot }).__tgBoot;
  const wa = app.wa as (typeof app.wa & { isExpanded?: boolean }) | null;
  let stored: string;
  try { stored = Object.keys(JSON.parse(sessionStorage.getItem('__telegram__initParams') ?? '{}') as object).join(', ') || '-'; } catch { stored = 'err'; }
  const inv = lastInvoice.value;
  const rows: [string, string][] = [
    ['build', BUILD_TAG],
    ['window.Telegram', tg ? 'yes' : 'NO'],
    ['Telegram.WebApp', tg?.WebApp ? 'yes' : 'NO'],
    ['app.wa', wa ? 'yes' : 'NULL'],
    ['initData length', String(wa?.initData.length ?? tg?.WebApp?.initData?.length ?? -1)],
    ['version / platform', `${wa?.version ?? tg?.WebApp?.version ?? '-'} / ${wa?.platform ?? tg?.WebApp?.platform ?? '-'}`],
    ['fullscreen / expanded', `${wa?.isFullscreen ? 1 : 0} / ${wa?.isExpanded ? 1 : 0}`],
    ['hash keys at start', boot?.hashKeys.join(', ') || '-'],
    ['telegram-web-app.js', `${boot?.script ?? '?'} @ ${boot?.scriptMs ?? '-'} ms`],
    ['waited for WebApp', `${bootWait.value ?? '-'} ms`],
    ['__telegram__initParams', stored],
    ['last openInvoice', inv ? `${inv.path}${inv.error ? ` (${inv.error})` : ''} → ${inv.status ?? 'waiting'}${inv.via ? ` via ${inv.via}` : ''}${inv.ms !== undefined ? ` in ${inv.ms} ms` : ''}${inv.fallback ? ' +link' : ''}` : '-'],
  ];
  return (
    <div class="debug-panel" data-testid="debug-panel" role="dialog" aria-label="debug">
      <table>{rows.map(([k, v]) => <tr key={k}><th>{k}</th><td>{v}</td></tr>)}</table>
      <button class="btn small outline" data-testid="debug-close" onClick={() => { debugOpen.value = false; }}>×</button>
    </div>
  );
}
